import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import portalInvoiceService from '../../services/portalInvoiceService';
import { FileText, DollarSign, AlertCircle, CheckCircle, Clock, Calendar } from 'lucide-react';

function PortalInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [invRes, sumRes] = await Promise.all([
          portalInvoiceService.getInvoices(),
          portalInvoiceService.getBillingSummary(),
        ]);
        setInvoices(invRes.data.data || []);
        setSummary(sumRes.data);
      } catch (err) {
        console.error('Failed to load invoices:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const formatCurrency = (amount) => `$${(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

  const statusConfig = {
    sent: { color: 'bg-blue-100 text-blue-800', icon: Clock, label: 'Pending' },
    paid: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Paid' },
    overdue: { color: 'bg-red-100 text-red-800', icon: AlertCircle, label: 'Overdue' },
    partial: { color: 'bg-yellow-100 text-yellow-800', icon: DollarSign, label: 'Partial' },
    void: { color: 'bg-gray-100 text-gray-800', icon: FileText, label: 'Void' },
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Invoices</h1>

      {/* Billing summary */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase">Total Billed</p>
            <p className="text-xl font-bold text-gray-900 mt-1">{formatCurrency(summary.total_billed)}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase">Total Paid</p>
            <p className="text-xl font-bold text-green-600 mt-1">{formatCurrency(summary.total_paid)}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase">Outstanding</p>
            <p className="text-xl font-bold text-orange-600 mt-1">{formatCurrency(summary.total_outstanding)}</p>
          </div>
        </div>
      )}

      {/* Invoice list */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {invoices.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <FileText className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p>No invoices to display.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {invoices.map((inv) => {
              const config = statusConfig[inv.status] || statusConfig.sent;
              return (
                <Link
                  key={inv.id}
                  to={`/portal/invoices/${inv.id}`}
                  className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      Invoice #{inv.invoice_number}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {inv.case_title && `${inv.case_title} · `}
                      Due {inv.due_date ? new Date(inv.due_date).toLocaleDateString() : 'N/A'}
                    </p>
                    {inv.has_payment_plan && inv.payment_plan_summary && (
                      <p className="text-xs text-blue-600 mt-0.5 inline-flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        Payment Plan &middot; {inv.payment_plan_summary.installments_completed} of {inv.payment_plan_summary.number_of_installments} paid
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                    <span className="text-sm font-semibold text-gray-900">
                      {formatCurrency(inv.total_amount)}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
                      {config.label}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default PortalInvoices;
